import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, School, Semester } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class SemesterController {
  /**
   * Create a new semester with comprehensive validation
   * @route POST /api/v1/school/semesters
   * @access Private (School Admin)
   */
  async createSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { semesterName, startDate, endDate, academicYear, description } =
        req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Validate school exists
      const school = await School.findById(schoolId);
      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Prompt academic year for this semester
      const calculatedAcademicYear =
        academicYear || this.generateAcademicYear(startDate, endDate);

      // Check for overlapping semesters ONLY within the same academic year
      // Allow multiple semesters across different academic years
      const overlappingSemester = await Semester.findOne({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        academicYear: calculatedAcademicYear,
        $or: [
          {
            startDate: { $lte: new Date(endDate) },
            endDate: { $gte: new Date(startDate) },
          },
        ],
        isActive: true,
      });

      if (overlappingSemester) {
        res.status(409).json({
          success: false,
          message: `A semester already exists during this period in academic year ${calculatedAcademicYear}: ${overlappingSemester.semesterName} (${overlappingSemester.startDate.toDateString()} - ${overlappingSemester.endDate.toDateString()})`,
          error: "Overlapping semester exists in same academic year",
        } as APIResponse);
        return;
      }

      // Check for duplicate semester name in same academic year
      const existingSemester = await Semester.findOne({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        semesterName,
        academicYear: calculatedAcademicYear,
      });

      if (existingSemester) {
        res.status(409).json({
          success: false,
          message: `A semester with name "${semesterName}" already exists for academic year ${calculatedAcademicYear}`,
          error: "Duplicate semester name",
        } as APIResponse);
        return;
      }

      // Create semester
      const semester = new Semester({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        semesterName,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        academicYear: calculatedAcademicYear,
        description,
        createdBy: new mongoose.Types.ObjectId(userId),
      });

      await semester.save();

      logger.info(`Semester created: ${semester.id}`, {
        semesterName,
        schoolId,
        userId,
        academicYear: semester.academicYear,
      });

      res.status(201).json({
        success: true,
        message: "Semester created successfully",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            academicYear: semester.academicYear,
            description: semester.description,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            createdAt: semester.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error creating semester:", error);

      // Handle duplicate semester name
      if (error.code === 11_000) {
        res.status(409).json({
          success: false,
          message:
            "A semester with this name already exists for this academic year",
          error: "Duplicate semester name",
        } as APIResponse);
        return;
      }

      // Handle validation errors
      if (error.name === "ValidationError") {
        const errors = Object.values(error.errors).map(
          (err: any) => err.message,
        );
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: errors.join(", "),
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to create semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all semesters for a school with filtering and pagination
   * @route GET /api/v1/school/semesters
   * @access Private (School Admin)
   */
  async getAllSemesters(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const {
        academicYear,
        status,
        isActive,
        page = 1,
        limit = 10,
        sortBy = "startDate",
        sortOrder = "desc",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Build query
      const query: any = { schoolId: new mongoose.Types.ObjectId(schoolId) };

      if (academicYear) query.academicYear = academicYear;
      if (status) query.status = status;
      if (isActive !== undefined) query.isActive = isActive === "true";

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [semesters, totalCount] = await Promise.all([
        // eslint-disable-next-line unicorn/no-array-callback-reference
        Semester.find(query)
          .sort(sort)
          .skip(skip)
          .limit(Number(limit))
          .populate("createdBy", "fullName email"),
        Semester.countDocuments(query),
      ]);

      // Get statistics
      const stats = await Semester.getStatistics(
        new mongoose.Types.ObjectId(schoolId),
      );

      res.status(200).json({
        success: true,
        message: `${semesters.length} semesters found`,
        data: {
          semesters: semesters.map(semester => ({
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            academicYear: semester.academicYear,
            description: semester.description,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
            createdBy: (semester as any).createdBy
              ? {
                  id: (semester as any).createdBy.id,
                  fullName: (semester as any).createdBy.fullName,
                  email: (semester as any).createdBy.email,
                }
              : null,
            createdAt: semester.createdAt,
            updatedAt: semester.updatedAt,
          })),
          pagination: {
            currentPage: Number(page),
            totalPages: Math.ceil(totalCount / Number(limit)),
            totalCount,
            hasNext: skip + semesters.length < totalCount,
            hasPrev: Number(page) > 1,
          },
          statistics: stats,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching semesters:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch semesters",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get semester by ID with detailed information
   * @route GET /api/v1/school/semesters/:id
   * @access Private (School Admin)
   */
  async getSemesterById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("createdBy", "fullName email")
        .populate(
          "classes",
          "className classCode subject status currentStudents maxStudents",
        );

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Get class statistics for this semester
      const classStats = await Class.aggregate([
        { $match: { semesterId: new mongoose.Types.ObjectId(id) } },
        {
          $group: {
            _id: null,
            totalClasses: { $sum: 1 },
            activeClasses: {
              $sum: { $cond: [{ $eq: ["$status", "active"] }, 1, 0] },
            },
            totalStudents: { $sum: "$currentStudents" },
            averageStudentsPerClass: { $avg: "$currentStudents" },
          },
        },
      ]);

      const stats = classStats[0] || {
        totalClasses: 0,
        activeClasses: 0,
        totalStudents: 0,
        averageStudentsPerClass: 0,
      };

      res.status(200).json({
        success: true,
        message: "Semester found",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            academicYear: semester.academicYear,
            description: semester.description,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
            createdBy: (semester as any).createdBy
              ? {
                  id: (semester as any).createdBy.id,
                  fullName: (semester as any).createdBy.fullName,
                  email: (semester as any).createdBy.email,
                }
              : null,
            classes: (semester as any).classes || [],
            classStatistics: stats,
            createdAt: semester.createdAt,
            updatedAt: semester.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update semester with comprehensive validation
   * @route PUT /api/v1/school/semesters/:id
   * @access Private (School Admin)
   */
  async updateSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const {
        semesterName,
        startDate,
        endDate,
        academicYear,
        description,
        status,
        isActive,
      } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Check for overlapping semesters (excluding current semester)
      if (startDate || endDate) {
        const newStartDate = startDate
          ? new Date(startDate)
          : semester.startDate;
        const newEndDate = endDate ? new Date(endDate) : semester.endDate;

        const overlappingSemester = await Semester.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          _id: { $ne: id },
          $or: [
            {
              startDate: { $lte: newEndDate },
              endDate: { $gte: newStartDate },
            },
          ],
          isActive: true,
        });

        if (overlappingSemester) {
          res.status(409).json({
            success: false,
            message: `A semester already exists during this period: ${overlappingSemester.semesterName}`,
            error: "Overlapping semester exists",
          } as APIResponse);
          return;
        }
      }

      // Check for duplicate semester name
      if (semesterName && semesterName !== semester.semesterName) {
        const existingSemester = await Semester.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          semesterName,
          academicYear: academicYear || semester.academicYear,
          _id: { $ne: id },
        });

        if (existingSemester) {
          res.status(409).json({
            success: false,
            message: `A semester with name "${semesterName}" already exists for academic year ${academicYear || semester.academicYear}`,
            error: "Duplicate semester name",
          } as APIResponse);
          return;
        }
      }

      // Update fields if provided
      if (semesterName !== undefined) semester.semesterName = semesterName;
      if (startDate !== undefined) semester.startDate = new Date(startDate);
      if (endDate !== undefined) semester.endDate = new Date(endDate);
      if (academicYear !== undefined) semester.academicYear = academicYear;
      if (description !== undefined) semester.description = description;
      if (status !== undefined) semester.status = status;
      if (isActive !== undefined) semester.isActive = isActive;

      await semester.save();

      logger.info(`Semester updated: ${semester.id}`, {
        semesterName: semester.semesterName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Semester updated successfully",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            academicYear: semester.academicYear,
            description: semester.description,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            updatedAt: semester.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error updating semester:", error);

      // Handle duplicate semester name
      if (error.code === 11_000) {
        res.status(409).json({
          success: false,
          message:
            "A semester with this name already exists for this academic year",
          error: "Duplicate semester name",
        } as APIResponse);
        return;
      }

      // Handle validation errors
      if (error.name === "ValidationError") {
        const errors = Object.values(error.errors).map(
          (err: any) => err.message,
        );
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: errors.join(", "),
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to update semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete semester with dependency checks
   * @route DELETE /api/v1/school/semesters/:id
   * @access Private (School Admin)
   */
  async deleteSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Check if semester is currently active
      if (semester.isActive) {
        res.status(400).json({
          success: false,
          message:
            "Cannot delete an active semester. Please deactivate it first.",
          error: "Active semester cannot be deleted",
        } as APIResponse);
        return;
      }

      // Check if semester has classes
      const classCount = await Class.countDocuments({ semesterId: id });

      if (classCount > 0) {
        res.status(400).json({
          success: false,
          message: `Cannot delete semester. It has ${classCount} classes assigned to it. Please reassign or delete the classes first.`,
          error: "Semester has dependencies",
          data: {
            classCount,
            classes: await Class.find({ semesterId: id })
              .select("className classCode subject status")
              .limit(5),
          },
        } as APIResponse);
        return;
      }

      await Semester.findByIdAndDelete(id);

      logger.info(`Semester deleted: ${semester.id}`, {
        semesterName: semester.semesterName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Semester deleted successfully",
        data: {
          deletedSemester: {
            id: semester.id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get current active semester
   * @route GET /api/v1/school/semesters/current
   * @access Private (School Admin)
   */
  async getCurrentSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findCurrent(
        new mongoose.Types.ObjectId(schoolId),
      );

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "No current semester found",
          error: "Current semester not found",
        } as APIResponse);
        return;
      }

      // Get class statistics for current semester
      const classStats = await Class.aggregate([
        { $match: { semesterId: semester._id } },
        {
          $group: {
            _id: null,
            totalClasses: { $sum: 1 },
            activeClasses: {
              $sum: { $cond: [{ $eq: ["$status", "active"] }, 1, 0] },
            },
            totalStudents: { $sum: "$currentStudents" },
          },
        },
      ]);

      const stats = classStats[0] || {
        totalClasses: 0,
        activeClasses: 0,
        totalStudents: 0,
      };

      res.status(200).json({
        success: true,
        message: "Current semester found",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            academicYear: semester.academicYear,
            description: semester.description,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
            classStatistics: stats,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching current semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch current semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Activate semester
   * @route PUT /api/v1/school/semesters/:id/activate
   * @access Private (School Admin)
   */
  async activateSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Check for overlapping active semesters
      const overlappingSemester = await Semester.findOne({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        _id: { $ne: id },
        $or: [
          {
            startDate: { $lte: semester.endDate },
            endDate: { $gte: semester.startDate },
          },
        ],
        isActive: true,
      });

      if (overlappingSemester) {
        res.status(409).json({
          success: false,
          message: `Cannot activate semester. Another active semester exists during this period: ${overlappingSemester.semesterName}`,
          error: "Overlapping active semester",
        } as APIResponse);
        return;
      }

      await semester.activate();

      logger.info(`Semester activated: ${semester.id}`, {
        semesterName: semester.semesterName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Semester activated successfully",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            status: semester.status,
            isActive: semester.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error activating semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to activate semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Deactivate semester
   * @route PUT /api/v1/school/semesters/:id/deactivate
   * @access Private (School Admin)
   */
  async deactivateSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      await semester.deactivate();

      logger.info(`Semester deactivated: ${semester.id}`, {
        semesterName: semester.semesterName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Semester deactivated successfully",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            status: semester.status,
            isActive: semester.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deactivating semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to deactivate semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Complete semester
   * @route PUT /api/v1/school/semesters/:id/complete
   * @access Private (School Admin)
   */
  async completeSemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const semester = await Semester.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      // Check if semester has ended
      if (!semester.hasEnded()) {
        res.status(400).json({
          success: false,
          message: "Cannot complete semester before its end date",
          error: "Semester not yet ended",
        } as APIResponse);
        return;
      }

      await semester.complete();

      logger.info(`Semester completed: ${semester.id}`, {
        semesterName: semester.semesterName,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Semester completed successfully",
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            status: semester.status,
            isActive: semester.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error completing semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to complete semester",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get semesters by academic year
   * @route GET /api/v1/school/semesters/academic-year/:academicYear
   * @access Private (School Admin)
   */
  async getSemestersByAcademicYear(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { academicYear } = req.params;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const semesters = await Semester.findByAcademicYear(
        academicYear,
        new mongoose.Types.ObjectId(schoolId),
      );

      res.status(200).json({
        success: true,
        message: `${semesters.length} semesters found for academic year ${academicYear}`,
        data: {
          academicYear,
          semesters: semesters.map(semester => ({
            id: semester.id,
            semesterName: semester.semesterName,
            startDate: semester.startDate,
            endDate: semester.endDate,
            status: semester.status,
            isActive: semester.isActive,
            durationInDays: semester.durationInDays,
            isCurrent: semester.isCurrent,
            hasStarted: semester.hasStarted(),
            hasEnded: semester.hasEnded(),
            createdAt: semester.createdAt,
          })),
          totalCount: semesters.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching semesters by academic year:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch semesters",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get semester statistics
   * @route GET /api/v1/school/semesters/statistics
   * @access Private (School Admin)
   */
  async getSemesterStatistics(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const statistics = await Semester.getStatistics(
        new mongoose.Types.ObjectId(schoolId),
      );

      // Get additional statistics
      const additionalStats = await Semester.aggregate([
        { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
        {
          $group: {
            _id: null,
            totalDuration: { $sum: "$durationInDays" },
            averageDuration: { $avg: "$durationInDays" },
            currentSemester: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      { $lte: ["$startDate", new Date()] },
                      { $gte: ["$endDate", new Date()] },
                      { $eq: ["$isActive", true] },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
          },
        },
      ]);

      const additional = additionalStats[0] || {
        totalDuration: 0,
        averageDuration: 0,
        currentSemester: 0,
      };

      res.status(200).json({
        success: true,
        message: "Semester statistics retrieved successfully",
        data: {
          statistics: {
            ...statistics,
            ...additional,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching semester statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Generate academic year from start and end dates
   * @private
   */
  private generateAcademicYear(
    startDate: string | Date,
    endDate: string | Date,
  ): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    return startYear === endYear ? `${startYear}` : `${startYear}-${endYear}`;
  }
}

// Export singleton instance
export const semesterController = new SemesterController();
